import { Cloudinary } from "@cloudinary/url-gen"
import { Resize } from "@cloudinary/url-gen/actions/resize"
import { useMemo } from "react"

import { Sliders, TeaserDimension, TTeaser } from "../components/Sliders/Slider"
import { PublicationLevel, SliderPageQuery } from "../generated/graphql"
import {
  mapSliderType,
  mapTeaserDimensions,
} from "../screens/Detail/DetailScreen"
import { appStore } from "../store/AppStore"
import { ArrayElement } from "../types"
import {
  CloudinaryConf,
  secondsToMinutes,
  s,
  ccFactor,
} from "../utilities/common"
import { locale } from "../utilities/constants"
import { isContentAvailable } from "../utilities/content-availability-helpers"

import useIntl from "./use-intl"
import useTranslate from "./use-translate"

const cld = new Cloudinary(CloudinaryConf)

export type SliderExtractV1 = Extract<
  SliderPageQuery["slider"],
  { __typename: "Slider" }
>

// Filter non-queried types
export type NodeExtractV1 = Exclude<
  ArrayElement<
    Exclude<SliderExtractV1["sliderItems"], undefined>["edges"]
  >["node"],
  {
    __typename:
      | "Work"
      | "Favorite"
      | "PlaybackProgress"
      | "CuratedContentLink"
      | "BlogArticleLink"
      | "Playback"
  }
>

type LiveConcertNodeV1 = Extract<
  NodeExtractV1,
  { __typename: "LiveConcert" }
> & { startTime: string }
const hasLiveConcertStartTime = (
  node: ArrayElement<
    Exclude<SliderExtractV1["sliderItems"], undefined>["edges"]
  >["node"],
): node is LiveConcertNodeV1 =>
  node.__typename === "LiveConcert" &&
  "startTime" in node &&
  typeof node.startTime === "string"

const getLiveTitles = (slider: SliderExtractV1) => {
  const dates: Record<number, number> = {}
  const formattedDates: Record<number, string> = {}
  slider.sliderItems?.edges
    .map((item) => item.node)
    .filter(hasLiveConcertStartTime)
    .map((node, i) => {
      const date = new Date(node.startTime)
      const formattedDate = date.getMonth()
      if (!Object.values(dates).includes(formattedDate)) {
        dates[i] = formattedDate
        const dateT = new Date()
        dateT.setMonth(formattedDate)
        formattedDates[i] = date.toLocaleString(locale, { month: "long" })
      }
    })
  return formattedDates
}

export const useSlidersV1 = (
  sliders?: Exclude<SliderPageQuery["slider"], undefined>[],
  withLiveData = false,
) => {
  const { dateFormat } = useIntl()
  const t = useTranslate()
  const geoInfo = appStore((state) => state.geoInfo)

  return useMemo((): Sliders | undefined => {
    if (!sliders) return undefined
    return sliders
      .filter((slider) => slider.__typename === "Slider")
      .map((slider: SliderExtractV1) => {
        if (!slider.sliderItems?.edges.length) return undefined
        return {
          id: slider.id,
          totalResults: slider.sliderItems.totalResults,
          title: slider.headline,
          layout: slider.layout,
          liveTitles:
            withLiveData &&
            slider.sliderItems.edges[0].node.__typename === "LiveConcert"
              ? getLiveTitles(slider)
              : undefined,
          type: mapSliderType(slider.sliderItems.edges[0].node.__typename),
          style: withLiveData
            ? TeaserDimension.LiveTeaser
            : mapTeaserDimensions(slider.sliderItems.edges[0].node.__typename),
          pageInfo: { hasNextPage: slider.sliderItems.pageInfo.hasNextPage },
          teasers: slider.sliderItems.edges
            // remove empty edges for safety
            .filter((edge) => "id" in edge.node)
            .filter(
              (edge) =>
                edge.node.__typename === "Video" ||
                edge.node.__typename === "VodConcert" ||
                edge.node.__typename === "LiveConcert" ||
                edge.node.__typename === "Album" ||
                edge.node.__typename === "EditorialPlaylist" ||
                edge.node.__typename === "Artist" ||
                edge.node.__typename === "Partner" ||
                edge.node.__typename === "Group" ||
                edge.node.__typename === "Epoch" ||
                edge.node.__typename === "Genre",
            )
            // remove dupes
            .filter(
              (value, index, self) =>
                index === self.findIndex((t) => t.node === value.node),
            )
            .filter((edge) => isContentAvailable(edge.node, geoInfo))
            .map((edge): TTeaser => {
              const node = edge.node
              return {
                id: "id" in node ? node.id : "",
                uniqueId: "id" in node ? node.id : "",
                type: node.__typename,
                title:
                  node.__typename === "VodConcert" ||
                  node.__typename === "Video" ||
                  node.__typename === "Album" ||
                  node.__typename === "EditorialPlaylist" ||
                  node.__typename === "LiveConcert" ||
                  node.__typename === "Epoch" ||
                  node.__typename === "Genre"
                    ? node.title
                    : node.__typename === "Artist" ||
                        node.__typename === "Partner" ||
                        node.__typename === "Group"
                      ? node.name
                      : "",
                subtitle:
                  node.__typename === "VodConcert" ||
                  node.__typename === "Video" ||
                  node.__typename === "LiveConcert" ||
                  node.__typename === "Epoch"
                    ? node.subtitle
                    : node.__typename === "Album"
                      ? node.artistAndGroupDisplayInfo
                      : node.__typename === "Artist"
                        ? node.roles.map((r) => r.displayName).join(", ")
                        : node.__typename === "Group"
                          ? node.typeDisplayName
                          : undefined,
                caption:
                  node.__typename === "LiveConcert" && node.streamStartTime
                    ? dateFormat(new Date(node.streamStartTime), "shortTime")
                    : node.__typename === "VodConcert"
                      ? t("duration__in_minutes", [
                          secondsToMinutes(node.totalDuration),
                        ])
                      : node.__typename === "Video"
                        ? t("duration__in_minutes", [
                            secondsToMinutes(node.duration),
                          ])
                        : undefined,
                liveConcert:
                  withLiveData && node.__typename === "LiveConcert"
                    ? node
                    : undefined,
                label:
                  node?.__typename === "VodConcert" ||
                  node?.__typename === "Video"
                    ? node.typeDisplayName
                    : node?.__typename === "EditorialPlaylist"
                      ? node.typeDisplayName
                      : undefined,
                publicationLevel:
                  node.__typename === "Video" ||
                  node.__typename === "VodConcert" ||
                  node.__typename === "LiveConcert"
                    ? node.publicationLevel
                    : PublicationLevel.TicketRequired,
                liveConcertType:
                  node.__typename === "LiveConcert"
                    ? node.liveConcertType
                    : undefined,
                picture:
                  node.__typename === "Video" ||
                  node.__typename === "VodConcert" ||
                  node.__typename === "LiveConcert" ||
                  node.__typename === "Album" ||
                  node.__typename === "EditorialPlaylist" ||
                  node.__typename === "Artist" ||
                  node.__typename === "Partner" ||
                  node.__typename === "Group" ||
                  node.__typename === "Epoch" ||
                  node.__typename === "Genre"
                    ? cld
                        .image(
                          node.pictures.filter((i) => {
                            return (
                              i.type ===
                              (node.__typename === "Album" ||
                              node.__typename === "EditorialPlaylist"
                                ? "COVER"
                                : node.__typename === "Partner"
                                  ? "LOGO"
                                  : "TEASER")
                            )
                          })[0]?.url,
                        )
                        .resize(Resize.scale().width(s(ccFactor(587), true)))
                        .format("jpg")
                        .setDeliveryType("fetch")
                        .toURL()
                    : undefined,
              }
            }),
        }
      })
      .filter((slider) => slider !== undefined)
  }, [sliders, dateFormat, t, geoInfo])
}
